import {
  EG_CITIES,
  EXPRESS_CITIES,
  GIFT_MESSAGE_MAX,
  LANGUAGES,
  LIMITS,
  PAYMENT_METHODS
} from '../../constants/storefrontOrderConstants';

// Gift message length mirrors the storefront form: 2-250 characters after trim.
const GIFT_MESSAGE_MIN = 2;

// Name character classes: letters (any script, so Arabic or Latin) plus spaces.
const KID_NAME_PATTERN = /^[\p{L} ]+$/u;
const ARABIC_PATTERN = /^[\u0600-\u06FF\s]+$/;
const LATIN_PATTERN = /^[A-Za-z\s]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate the kid's name, mirroring the storefront order form rules:
 * 2-15 characters after trim, letters and spaces only, and the name's
 * script must not be the "opposite" of the story language (a Latin-only
 * name is rejected on an Arabic story and vice versa).
 *
 * @param {string} name - Raw kid name input.
 * @param {string} languageCode - Story language code (e.g. 'en', 'ar').
 * @returns {string|null} Error message, or null when valid.
 */
export function validateKidName(name, languageCode) {
  const trimmed = (name || '').trim();

  if (trimmed.length < LIMITS.KID_NAME_MIN || trimmed.length > LIMITS.KID_NAME_MAX) {
    return `Name must be between ${LIMITS.KID_NAME_MIN} and ${LIMITS.KID_NAME_MAX} characters.`;
  }

  if (!KID_NAME_PATTERN.test(trimmed)) {
    return 'Name may contain letters and spaces only (no digits or symbols).';
  }

  const isArabicStory = languageCode === 'ar';
  if (isArabicStory && !ARABIC_PATTERN.test(trimmed)) {
    return 'A Latin name is not allowed on an Arabic story. Use an Arabic name.';
  }

  if (!isArabicStory && !LATIN_PATTERN.test(trimmed)) {
    return 'An Arabic name is not allowed on a non-Arabic story. Use a Latin name.';
  }

  return null;
}

/**
 * Validate a single story entry: story, variant, 1-4 finished photos, and
 * the gift-message addon. Only validated when the gift note addon is on.
 *
 * Entry shape: {
 *   key: string, story: Object, variant: Object, languageCode: string,
 *   kidName: string, photos: Array<{ id, status, url }>,
 *   giftBox: boolean, giftNote: boolean, giftMessage: string,
 *   premiumPrinting: boolean
 * }
 *
 * @param {Object} entry - Entry shape from the page state.
 * @returns {string[]} Error messages (empty when valid).
 */
export function validateEntry(entry) {
  const errors = [];

  if (!entry.story) {
    errors.push('Select a story.');
  }

  if (!entry.variant) {
    errors.push('Select a size.');
  }

  const kidNameError = validateKidName(entry.kidName, entry.languageCode);
  if (kidNameError) {
    errors.push(kidNameError);
  }

  const photos = entry.photos || [];
  if (photos.length === 0) {
    errors.push('Upload at least one photo.');
  } else if (photos.length > LIMITS.MAX_PHOTOS) {
    errors.push(`You can upload up to ${LIMITS.MAX_PHOTOS} photos per story.`);
  } else if (photos.some(photo => photo.status !== 'done')) {
    errors.push('Wait for all photos to finish uploading.');
  }

  if (entry.giftNote) {
    const trimmed = (entry.giftMessage || '').trim();
    if (trimmed.length < GIFT_MESSAGE_MIN || trimmed.length > GIFT_MESSAGE_MAX) {
      errors.push(
        `Gift message must be between ${GIFT_MESSAGE_MIN} and ${GIFT_MESSAGE_MAX} characters.`
      );
    }
  }

  // Premium printing is only offered on variants the catalog flags with
  // premium data (a matching premium variant in Shopify).
  if (entry.premiumPrinting && !(entry.variant && entry.variant.premium)) {
    errors.push('This story does not support premium printing.');
  }

  // Legacy stories are premium printing only. When the story has a premium
  // variant, the size must be premium. Without one, the backend marks the
  // line premium at its standard price, so no error is needed.
  if (
    entry.story &&
    entry.story.story_type === 'legacy_static' &&
    entry.variant &&
    entry.variant.premium &&
    !entry.premiumPrinting
  ) {
    errors.push('Legacy stories are premium printing only. Choose a size with premium printing.');
  }

  return errors;
}

/**
 * Validate the customer fields, mirroring the storefront order form.
 *
 * @param {Object} customer - { name, phone, email, address, city }.
 * @returns {Object} Map of field name to error message (empty when valid).
 */
export function validateCustomer(customer) {
  const errors = {};

  const name = (customer.name || '').trim();
  if (name.split(/\s+/).filter(Boolean).length < 2) {
    errors.name = 'Please enter your full name (first and last).';
  }

  const digits = (customer.phone || '').replace(/\D/g, '');
  if (digits.length < 8) {
    errors.phone = 'Please enter a valid phone number.';
  }

  if (!EMAIL_PATTERN.test((customer.email || '').trim())) {
    errors.email = 'Please enter a valid email address.';
  }

  if (!(customer.address || '').trim()) {
    errors.address = 'Please enter your address.';
  }

  if (!EG_CITIES.includes(customer.city)) {
    errors.city = 'Please choose your city from the list.';
  }

  return errors;
}

/**
 * Split a full customer name into first name and everything else, in the
 * same way the storefront form fills first/last name fields.
 *
 * @param {string} name - Raw full name.
 * @returns {{ firstName: string, lastName: string }}
 */
export function splitCustomerName(name) {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return { firstName: '', lastName: '' };
  }
  return { firstName: words[0], lastName: words.slice(1).join(' ') };
}

/**
 * Express shipping is only available in Cairo and Giza; choosing it
 * elsewhere is a soft warning, not a blocker.
 *
 * @param {string} shippingValue - 'regular' | 'express'.
 * @param {string} city - Customer city.
 * @returns {boolean}
 */
export function isExpressCityMismatch(shippingValue, city) {
  return shippingValue === 'express' && !EXPRESS_CITIES.includes(city);
}

/**
 * Validate the whole order form.
 *
 * @param {Object} params
 * @param {Object[]} params.entries - Entry list.
 * @param {Object} params.customer - Customer fields.
 * @param {string} params.paymentMethod - Selected payment method value.
 * @returns {{ valid: boolean, entryErrors: Record<string, string[]>, customerErrors: Object, formErrors: string[] }}
 */
export function validateForm({ entries, customer, paymentMethod }) {
  const entryErrors = {};
  (entries || []).forEach((entry, index) => {
    const errors = validateEntry(entry);
    if (errors.length > 0) {
      entryErrors[(entry && entry.key) || String(index)] = errors;
    }
  });

  const customerErrors = validateCustomer(customer || {});

  const formErrors = [];
  if (!entries || entries.length === 0) {
    formErrors.push('Add at least one story to the order.');
  } else if (entries.length > LIMITS.MAX_STORIES) {
    formErrors.push(`An order can include up to ${LIMITS.MAX_STORIES} stories.`);
  }

  if (!PAYMENT_METHODS.some(method => method.value === paymentMethod)) {
    formErrors.push('Please choose a payment method.');
  }

  const valid =
    formErrors.length === 0 &&
    Object.keys(entryErrors).length === 0 &&
    Object.keys(customerErrors).length === 0;

  return { valid, entryErrors, customerErrors, formErrors };
}
